<script setup lang="ts">
import { computed, useSlots } from 'vue';
import { computedAsync } from '@vueuse/core';

import { isExperimentAsync } from '@libs/feature-toggle';

import { AppLoadingDots, AppSpinner } from '../appLoaders/index';
import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';

import type {
  AppButtonIconProperties,
  AppButtonSize,
  AppButtonState,
  AppButtonType,
} from './AppButton.model';

interface AppButtonProps {
  type: AppButtonType;
  state: AppButtonState;
  size: AppButtonSize;
  noBorder?: boolean;
  icon?: AppButtonIconProperties;
  wide?: boolean;
  ariaLabel?: string;
}

const props = defineProps<AppButtonProps>();

interface AppButtonEmits {
  (eventName: 'click', event: MouseEvent): void;
}

const emit = defineEmits<AppButtonEmits>();

const slots = useSlots();
const hasTextSlot = computed(() => !!slots['default']);

const iconSize = computed(() => {
  if (props.icon && props.icon.size) {
    return props.icon.size;
  } else if (props.size === 'small') {
    return '18';
  } else {
    return '24';
  }
});

function onClick(event: MouseEvent): void {
  if (props.state) {
    emit('click', event);
  }
}

const isNewBookTypeButtonExperiment = computedAsync(
  () => isExperimentAsync('new_book_type_button'),
  false
);
</script>

<template>
  <button
    class="app-button"
    :disabled="props.state === 'disabled'"
    :class="[
      [
        props.type === 'important' && isNewBookTypeButtonExperiment
          ? 'important-experiment'
          : props.type,
      ],
      [props.size],
      [props.state],
      props.icon?.position === 'left' ? 'left-icon' : 'right-icon',
      props.wide ? 'wide' : undefined,
      props.noBorder ? 'no-border' : undefined,
      hasTextSlot ? undefined : 'square',
    ]"
    :aria-label="props.ariaLabel"
    @click="onClick"
  >
    <AppSpinner v-if="props.state === 'loadingSpinner'" />
    <AppLoadingDots v-else-if="props.state === 'loadingDots'" type="white" />
    <template v-else>
      <AppSvgIcon v-if="props.icon" :name="props.icon.name" :size="iconSize" />
      <div v-if="hasTextSlot" class="button-text">
        <slot />
      </div>
    </template>
  </button>
</template>

<style scoped lang="scss">
@import '../../styles/_media';
@import '../../styles/_fonts.scss';

@mixin disabledState {
  &.loadingDots,
  &.loadingSpinner,
  &:disabled {
    background-color: var(--color-neutral-30);
    color: var(--color-neutral-50);
    cursor: default;
    pointer-events: none;
    border: 1px solid transparent;
  }
}

.app-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-4);
  border-radius: var(--border-radius-8);
  white-space: nowrap;
  transition-property: background-color, transform;
  transition-duration: var(--transition-time-medium);
  transition-timing-function: ease-out;

  @include on-hover {
    cursor: pointer;
  }

  &:active {
    transform: matrix(0.95, 0, 0, 0.95, 0, 0);
  }

  &.wide {
    width: 100%;
  }

  // Types styling
  &.important {
    background-color: var(--color-information-dark);
    color: var(--color-white);
    border: 1px solid transparent;
    text-decoration: none;

    @include on-hover {
      background-color: var(--color-brand-violet);
    }

    &:focus-visible {
      border-color: var(--color-white);
      outline: solid 2px var(--color-neutral-70);
    }

    @include disabledState;
  }

  &.important-experiment {
    --color-hover-important-experiment: #154747;
    --color-focus-important-experiment: #2b9696;
    background-color: var(--color-brand-jungle);
    color: var(--color-white);
    text-decoration: none;
    border: none;
    @include on-hover {
      background-color: var(--color-hover-important-experiment);
    }

    &:focus-visible {
      color: var(--color-white);
      border: solid 2px var(--color-white);
      outline: 2px solid var(--color-neutral-70);
      background-color: var(--color-focus-important-experiment);
    }

    @include disabledState;
  }

  &.primary {
    background-color: var(--color-neutral-70);
    color: var(--color-white);
    border: 1px solid transparent;
    text-decoration: none;

    @include on-hover {
      background-color: var(--color-neutral-70);
    }

    &:focus-visible {
      border-color: var(--color-white);
      outline: solid 2px var(--color-neutral-70);
    }

    @include disabledState;
  }

  &.secondary {
    background-color: var(--color-white);
    color: var(--color-neutral-70);
    border: 1px solid var(--color-neutral-70);
    text-decoration: none;

    @include on-hover {
      background-color: var(--color-neutral-20);
    }

    &:focus-visible {
      border: 1px solid transparent;
      outline: solid 2px var(--color-neutral-70);
    }
  }

  &.modern {
    background-color: var(--color-white);
    color: var(--color-neutral-70);
    border: 1px solid var(--color-scrollbar);
    text-decoration: none;

    @include on-hover {
      background-color: var(--color-neutral-10);
    }

    &:focus-visible {
      border: 1px solid transparent;
      outline: solid 2px var(--color-soft-black);
      background-color: var(--color-white);
    }
  }

  &.text {
    background-color: transparent;
    color: var(--color-neutral-70);
    border: 1px solid transparent;
    text-decoration: underline;

    @include on-hover {
      background-color: var(--color-neutral-10);
    }

    &:focus-visible {
      border: 1px solid transparent;
    }

    @include disabledState;
    &:disabled {
      background-color: transparent;
    }
  }

  // Sizes
  &.small {
    --size-small: 36px;
    height: var(--size-small);
    @include font-button-small;
    padding: 0 var(--spacing-12);

    &.square {
      min-width: var(--size-small);
      padding: 0;
    }
  }

  &.medium {
    --size-medium: 44px;
    height: var(--size-medium);
    @include font-button-medium;
    padding: 0 var(--spacing-24);

    &.square {
      min-width: var(--size-medium);
      padding: 0;
    }
  }

  &.large {
    --size-large: 56px;
    height: var(--size-large);
    @include font-button-large;
    padding: 0 var(--spacing-24);

    &.square {
      min-width: var(--size-large);
      padding: 0;
    }
  }

  &.no-border {
    border: none;
  }

  // Alignment
  &.left-icon {
    flex-direction: row-reverse;

    &.align-left {
      justify-content: flex-end;
    }

    &.align-right {
      justify-content: flex-start;
    }
  }

  &.right-icon {
    flex-direction: row;
  }

  &.align-left {
    justify-content: flex-start;
  }

  &.align-right {
    justify-content: flex-end;
  }
}
</style>
